/**
 * Connection validation utilities for switchboard nodes
 * Implements validation rules to prevent prohibited self-connections
 * matching legacy behavior exactly.
 */
import {
  NodeType,
  type SwitchboardNode,
} from '../converters/shared/reactFlowTypes'
import type { SwitchboardTranslateFn } from '../types'
import { text } from './text'

/**
 * Known handle patterns for React Flow format detection
 * Used to identify if a dash-separated suffix is a handle ID
 */
const HANDLE_PATTERNS = [
  'on',
  'when',
  'option-',
  'noAnswer',
  'busy',
  'holiday',
] as const

/**
 * Extract node ID from various formats
 * Handles option endpoint IDs and React Flow formats
 */
export function extractNodeId(nodeId: string): string {
  // Handle option endpoint format: "123456789_option-1111111111111" → "123456789"
  if (nodeId.indexOf('_option-') !== -1) {
    return nodeId.split('_')[0]
  }

  // Handle React Flow format if handles include node ID: "123456789-onTimeout" → "123456789"
  // This is defensive - React Flow typically separates node ID and handle ID
  const dashIndex = nodeId.indexOf('-')
  if (dashIndex !== -1) {
    // Check if it looks like a handle ID (contains camelCase handle names)
    const afterDash = nodeId.substring(dashIndex + 1)
    // If it's a known handle pattern, extract node ID
    if (HANDLE_PATTERNS.some((pattern) => afterDash.startsWith(pattern))) {
      return nodeId.substring(0, dashIndex)
    }
  }

  // Standard node ID
  return nodeId
}

/**
 * Check if a node type requires self-connection validation
 * Returns true for 16 node types that have restrictions
 */
export function requiresSelfConnectionValidation(nodeType: string): boolean {
  const validatedNodeTypes = [
    'queue',
    'wait',
    'sound_clip',
    'day_night',
    'schedule',
    'external_number',
    'external_transfer', // alias for external_number
    'ai_agent_v1',
    'transfer',
    'direct_line', // transfer is an alias for direct_line
    'transfer_rollover',
    'transfer_and_rollover', // React Flow name
    'simple_dial',
    'transfer_and_continue', // React Flow name
    'dial_name',
    'dial_by_name', // React Flow name
    'webhook',
    'change_caller_id',
    'fax_detect',
    'auto_attendant',
    'interactive_webhook',
    'call_distribution',
    'sms_notification',
  ]

  return validatedNodeTypes.includes(nodeType)
}

/**
 * Check if an endpoint is restricted for a given node type
 * For conditionally-restricted nodes, only specific endpoints are restricted
 */
export function isRestrictedEndpoint(
  nodeType: string,
  endpointLabel: string,
): boolean {
  // auto_attendant: Only "On Timeout" is restricted
  if (nodeType === 'auto_attendant') {
    return endpointLabel === 'On Timeout'
  }

  // interactive_webhook: "On Invalid" and "On Timeout" are restricted
  if (nodeType === 'interactive_webhook') {
    return endpointLabel === 'On Invalid' || endpointLabel === 'On Timeout'
  }

  // All other validated node types: All endpoints are restricted
  return true
}

/**
 * Format node type name for error messages
 * Maps node types to their display names matching legacy format
 */
export function formatNodeNameForError(nodeType: string): string {
  const nameMappings: Record<string, string> = {
    simple_dial: 'Transfer And Continue',
    transfer_and_continue: 'Transfer And Continue',
    start_node: 'Start',
    start: 'Start',
    direct_line: 'Transfer',
    transfer: 'Transfer',
    dial_name: 'Dial By Name',
    dial_by_name: 'Dial By Name',
    transfer_rollover: 'Transfer And Rollover',
    transfer_and_rollover: 'Transfer And Rollover',
    webhook: 'Webhook',
    fax_detect: 'Fax Detect',
    call_distribution: 'Call Distribution',
    auto_attendant: 'Auto Attendant',
    interactive_webhook: 'Interactive Webhook',
    sms_notification: 'SMS Notification',
  }

  if (nameMappings[nodeType]) {
    return nameMappings[nodeType]
  }

  // Default: Capitalize and add spaces
  return nodeType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Fixed handle mappings by node type
 * Maps React Flow handle IDs to display labels
 */
const FIXED_HANDLE_MAPPINGS: Record<string, Record<string, string>> = {
  schedule: {
    whenOpen: 'Open',
    whenClosed: 'Closed',
    holiday: 'Holiday',
  },
  day_night: {
    whenOpen: 'When Open',
    whenClosed: 'When Closed',
  },
  queue: {
    onTimeout: 'On Timeout',
    onEscape: 'On Escape',
  },
  transfer_and_rollover: {
    noAnswer: 'No Answer',
    busy: 'Busy',
  },
  transfer_rollover: {
    noAnswer: 'No Answer',
    busy: 'Busy',
  },
  ai_agent_v1: {
    onError: 'On Error',
  },
} as const

/**
 * Custom handle mappings for auto_attendant and interactive_webhook
 */
const CUSTOM_HANDLE_MAPPINGS: Record<string, string> = {
  onInvalid: 'On Invalid',
  onTimeout: 'On Timeout',
} as const

/**
 * Memoization cache for getEndpointLabelFromHandle
 * Key: `${nodeType}:${sourceHandle}`
 */
const endpointLabelCache = new Map<string, string | undefined>()

/**
 * Map React Flow handle IDs to endpoint display labels
 * Handles all handle types: fixed handles, custom handles, and option handles
 * Uses memoization for performance during drag operations
 */
export function getEndpointLabelFromHandle(
  nodeType: string,
  sourceHandle: string | null | undefined,
  _nodeData?: Record<string, unknown>,
): string | undefined {
  if (!sourceHandle) {
    return undefined
  }

  // Check cache first
  const cacheKey = `${nodeType}:${sourceHandle}`
  if (endpointLabelCache.has(cacheKey)) {
    return endpointLabelCache.get(cacheKey)
  }

  let result: string | undefined

  // Check fixed handles
  const nodeMapping = FIXED_HANDLE_MAPPINGS[nodeType]
  if (nodeMapping && nodeMapping[sourceHandle]) {
    result = nodeMapping[sourceHandle]
  }
  // Custom handles for auto_attendant and interactive_webhook
  else if (
    nodeType === 'auto_attendant' ||
    nodeType === 'interactive_webhook'
  ) {
    if (CUSTOM_HANDLE_MAPPINGS[sourceHandle]) {
      result = CUSTOM_HANDLE_MAPPINGS[sourceHandle]
    }
    // Option handles (option-0, option-1, etc.)
    // For conditionally-restricted nodes, options are NOT restricted
    else if (sourceHandle.startsWith('option-')) {
      result = undefined
    }
    // Unknown handle - return undefined (will be treated as non-restricted)
    else {
      result = undefined
    }
  }
  // Option handles for other node types
  else if (sourceHandle.startsWith('option-')) {
    result = undefined
  }
  // Unknown handle - return undefined (will be treated as non-restricted)
  else {
    result = undefined
  }

  // Cache result
  endpointLabelCache.set(cacheKey, result)
  return result
}

/**
 * Clear the endpoint label cache
 * Useful for testing or when node configurations change
 */
export function clearEndpointLabelCache(): void {
  endpointLabelCache.clear()
}

/**
 * Edge interface for connection validation
 * Minimal interface to avoid circular dependencies
 */
export interface SwitchboardEdge {
  id: string
  source: string
  target: string
  sourceHandle?: string | null
  targetHandle?: string | null
}

/**
 * Check if a source handle already has an outgoing connection
 * Returns the existing edge if found
 */
export function hasExistingConnection(
  sourceNodeId: string,
  sourceHandle: string | null | undefined,
  edges: SwitchboardEdge[],
): { hasConnection: boolean; existingEdge?: SwitchboardEdge } {
  // Normalize sourceHandle to null if undefined
  const normalizedHandle = sourceHandle === undefined ? null : sourceHandle

  // Find edge with matching source and sourceHandle
  const existingEdge = edges.find((edge) => {
    const edgeHandle =
      edge.sourceHandle === undefined ? null : edge.sourceHandle
    return edge.source === sourceNodeId && edgeHandle === normalizedHandle
  })

  return {
    hasConnection: !!existingEdge,
    existingEdge,
  }
}

/**
 * Validation result interface
 */
export interface ConnectionValidationResult {
  valid: boolean
  errorMessage?: string
  existingEdge?: {
    id: string
    source: string
    target: string
    sourceHandle: string | null
  } // Add existing edge info
}

/**
 * Build adjacency list from existing edges for graph traversal.
 */
function buildAdjacencyMap(edges: SwitchboardEdge[]): Map<string, Set<string>> {
  const adjacency = new Map<string, Set<string>>()

  edges.forEach((edge) => {
    const sourceId = extractNodeId(edge.source)
    const targetId = extractNodeId(edge.target)

    if (!adjacency.has(sourceId)) {
      adjacency.set(sourceId, new Set<string>())
    }
    adjacency.get(sourceId)?.add(targetId)
  })

  return adjacency
}

/**
 * Check if a path exists between two nodes in a directed graph.
 */
function hasDirectedPath(
  adjacency: Map<string, Set<string>>,
  startNodeId: string,
  targetNodeId: string,
): boolean {
  if (startNodeId === targetNodeId) {
    return true
  }

  const visited = new Set<string>([startNodeId])
  const stack = [startNodeId]

  while (stack.length > 0) {
    const currentNodeId = stack.pop()
    if (!currentNodeId) continue

    const neighbors = adjacency.get(currentNodeId)
    if (!neighbors) continue

    for (const neighborId of neighbors) {
      if (neighborId === targetNodeId) {
        return true
      }

      if (!visited.has(neighborId)) {
        visited.add(neighborId)
        stack.push(neighborId)
      }
    }
  }

  return false
}

/**
 * Determine if adding source -> target would create a directed cycle.
 * A cycle is created when target can already reach source.
 */
function createsCallLoop(
  sourceNodeId: string,
  targetNodeId: string,
  edges: SwitchboardEdge[],
): boolean {
  const normalizedSourceId = extractNodeId(sourceNodeId)
  const normalizedTargetId = extractNodeId(targetNodeId)
  // Self-connection checks are handled in validateConnection before this helper.

  if (edges.length === 0) {
    return false
  }

  const adjacency = buildAdjacencyMap(edges)
  return hasDirectedPath(adjacency, normalizedTargetId, normalizedSourceId)
}

/**
 * Allow a narrow cyclical exception for:
 * Auto Attendant (option) -> Sound Clip -> same Auto Attendant.
 *
 * Conditions:
 * - The attempted connection is between auto_attendant and sound_clip.
 * - A direct reverse edge already exists between the same two nodes.
 * - The AA-originating edge uses an option handle (option-*).
 * - No alternate multi-node path exists from target -> source once direct reverse
 *   edges between this pair are removed.
 */
function isAllowedAutoAttendantSoundClipTwoNodeLoop(
  sourceNode: SwitchboardNode,
  targetNode: SwitchboardNode,
  sourceHandle: string | null | undefined,
  edges: SwitchboardEdge[],
): boolean {
  const sourceNodeType = sourceNode.type
  const targetNodeType = targetNode.type

  const isAutoAttendantToSoundClip =
    sourceNodeType === NodeType.AutoAttendant &&
    targetNodeType === NodeType.SoundClip
  const isSoundClipToAutoAttendant =
    sourceNodeType === NodeType.SoundClip &&
    targetNodeType === NodeType.AutoAttendant

  if (!isAutoAttendantToSoundClip && !isSoundClipToAutoAttendant) {
    return false
  }

  const normalizedSourceId = extractNodeId(sourceNode.id)
  const normalizedTargetId = extractNodeId(targetNode.id)

  // Reverse edge must already exist between this exact pair.
  const reverseEdges = edges.filter((edge) => {
    return (
      extractNodeId(edge.source) === normalizedTargetId &&
      extractNodeId(edge.target) === normalizedSourceId
    )
  })

  if (reverseEdges.length === 0) {
    return false
  }

  const reverseOptionEdges = reverseEdges.filter(
    (edge) => !!edge.sourceHandle && edge.sourceHandle.startsWith('option-'),
  )

  // The AA-originating edge must be an option handle (option-*).
  const hasAutoAttendantOptionHandle = isAutoAttendantToSoundClip
    ? !!sourceHandle && sourceHandle.startsWith('option-')
    : reverseOptionEdges.length > 0

  if (!hasAutoAttendantOptionHandle) {
    return false
  }

  // For sound_clip -> auto_attendant, reverse edges are AA -> Sound Clip.
  // If any reverse edge is not option-*, this would also permit timeout/invalid
  // loop paths, which must remain blocked.
  if (isSoundClipToAutoAttendant) {
    const hasNonOptionReverseEdge =
      reverseOptionEdges.length !== reverseEdges.length
    if (hasNonOptionReverseEdge) {
      return false
    }
  }

  // Keep broader loops blocked: exclude direct reverse edges and require no
  // alternative target -> source path through other nodes.
  const edgesWithoutDirectReverse = edges.filter((edge) => {
    return !(
      extractNodeId(edge.source) === normalizedTargetId &&
      extractNodeId(edge.target) === normalizedSourceId
    )
  })

  if (edgesWithoutDirectReverse.length === 0) {
    return true
  }

  const adjacency = buildAdjacencyMap(edgesWithoutDirectReverse)
  return !hasDirectedPath(adjacency, normalizedTargetId, normalizedSourceId)
}

/**
 * Main validation function
 * Validates if a connection is allowed between source and target nodes
 */
export function validateConnection(
  sourceNode: SwitchboardNode | null | undefined,
  targetNode: SwitchboardNode | null | undefined,
  sourceHandle?: string | null,
  sourceEndpointLabel?: string,
  edges: SwitchboardEdge[] = [], // Required for connection-limit validation; default avoids breaking legacy callers
  t?: SwitchboardTranslateFn,
): ConnectionValidationResult {
  // Missing nodes: return valid to avoid breaking React Flow
  if (!sourceNode || !targetNode) {
    return { valid: true }
  }

  const sourceNodeType = sourceNode.type
  const targetNodeType = targetNode.type
  const sourceNodeId = extractNodeId(sourceNode.id)
  const targetNodeId = extractNodeId(targetNode.id)

  // Check for existing connection from this handle (connection limit validation)
  if (edges.length > 0 && sourceNode) {
    const { hasConnection, existingEdge } = hasExistingConnection(
      sourceNode.id,
      sourceHandle,
      edges,
    )

    if (hasConnection) {
      // Get endpoint label for error message
      let endpointLabel = sourceEndpointLabel
      if (!endpointLabel && sourceHandle) {
        endpointLabel = getEndpointLabelFromHandle(
          sourceNodeType,
          sourceHandle,
          sourceNode.data as Record<string, unknown>,
        )
      }

      const nodeName = formatNodeNameForError(sourceNodeType)
      const handleText = endpointLabel ? ` "${endpointLabel}"` : ''
      const errorMessage = text(
        t,
        'switchboard.validation.connection.anchorAlreadyConnected',
        'The {{nodeName}}{{handleText}} anchor already has a connection. Remove the existing connection first.',
        {
          nodeName,
          handleText,
        },
      )

      return {
        valid: false,
        errorMessage,
        existingEdge: existingEdge
          ? {
              id: existingEdge.id,
              source: existingEdge.source,
              target: existingEdge.target,
              sourceHandle: existingEdge.sourceHandle ?? null,
            }
          : undefined,
      }
    }
  }

  if (sourceNodeId === targetNodeId) {
    if (!requiresSelfConnectionValidation(sourceNodeType)) {
      return { valid: true }
    }

    // Same node - check endpoint restrictions
    // Get endpoint label if not provided
    let endpointLabel = sourceEndpointLabel
    if (!endpointLabel && sourceHandle) {
      endpointLabel = getEndpointLabelFromHandle(
        sourceNodeType,
        sourceHandle,
        sourceNode.data as Record<string, unknown>,
      )
    }

    // For conditionally-restricted nodes, check endpoint-specific restrictions
    if (
      sourceNodeType === 'auto_attendant' ||
      sourceNodeType === 'interactive_webhook'
    ) {
      // interactive_webhook options cannot loop back to its own start anchor.
      if (
        sourceNodeType === 'interactive_webhook' &&
        !!sourceHandle &&
        sourceHandle.startsWith('option-')
      ) {
        const nodeName = formatNodeNameForError(sourceNodeType)
        const errorMessage = text(
          t,
          'switchboard.validation.connection.selfConnection',
          'You cannot connect the {{nodeName}} endpoint to itself.',
          { nodeName },
        )
        return {
          valid: false,
          errorMessage,
        }
      }

      if (!endpointLabel && !sourceHandle) {
        const nodeName = formatNodeNameForError(sourceNodeType)
        const errorMessage = text(
          t,
          'switchboard.validation.connection.selfConnection',
          'You cannot connect the {{nodeName}} endpoint to itself.',
          { nodeName },
        )
        return {
          valid: false,
          errorMessage,
        }
      }

      if (!endpointLabel) {
        return { valid: true }
      }

      if (!isRestrictedEndpoint(sourceNodeType, endpointLabel)) {
        return { valid: true }
      }
    }

    const nodeName = formatNodeNameForError(sourceNodeType)
    const errorMessage = text(
      t,
      'switchboard.validation.connection.selfConnection',
      'You cannot connect the {{nodeName}} endpoint to itself.',
      { nodeName },
    )
    return {
      valid: false,
      errorMessage,
    }
  }

  if (createsCallLoop(sourceNode.id, targetNode.id, edges)) {
    if (
      isAllowedAutoAttendantSoundClipTwoNodeLoop(
        sourceNode,
        targetNode,
        sourceHandle,
        edges,
      )
    ) {
      return { valid: true }
    }

    const sourceNodeName = formatNodeNameForError(sourceNodeType)
    const targetNodeName = formatNodeNameForError(targetNodeType)
    const errorMessage = text(
      t,
      'switchboard.validation.connection.createsCallLoop',
      'You cannot connect {{sourceNodeName}} to {{targetNodeName}} because it creates a call loop.',
      {
        sourceNodeName,
        targetNodeName,
      },
    )

    return {
      valid: false,
      errorMessage,
    }
  }

  return { valid: true }
}
